// The quiz station's container: owns the quiz's local state (answers,
// screen position, localStorage persistence) and the per-question
// analytics, and defers to the engine for every navigation decision. The
// host (QuoteFlow.tsx) owns the URL and the server sync — this component
// reports every commit upward and never talks to the API itself.

import { useEffect, useRef, useState } from "react";

import { useQuoteFlowAnalytics } from "pages/QuoteFlow/analytics";
import type { QuizResponseAnimationVariant } from "pages/QuoteFlow/flags";

import {
  QUIZ_COMPLETE,
  nextScreen,
  previousScreen,
  questionBySlug,
  slugFor,
} from "./engine";
import { QUIZ_QUESTIONS } from "./questions";
import QuizProgressBar from "./QuizProgressBar";
import QuizQuestionScreen from "./QuizScreen";
import { loadQuiz, saveQuiz } from "./store";
import type { Answers } from "./types";

function stepNumber(key: string): number {
  const index = QUIZ_QUESTIONS.findIndex((question) => question.key === key);
  return index === -1 ? 0 : index + 1;
}

/** How long the concluding hold waits before offering a manual retry —
 * long enough that a normal round trip never shows it, short enough that
 * a real failure (the host's sync couldn't reach the server) doesn't
 * strand the family looking at a spinner with no way out. */
const ADVANCE_RETRY_AFTER_MS = 8_000;

function AwaitingAdvanceScreen({
  showRetry,
  onRetry,
}: {
  showRetry: boolean;
  onRetry: () => void;
}) {
  return (
    <main className="flex flex-1 flex-col items-center justify-center gap-4 px-5 py-16">
      <div
        role="status"
        aria-label="Loading"
        className="h-8 w-8 animate-spin rounded-full border-[3px] border-oat-300 border-t-evergreen"
      />
      <p className="text-base text-oat-700">One moment&hellip;</p>
      {showRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="text-base font-bold text-evergreen underline underline-offset-4 hover:text-plum"
        >
          Taking a while — try again
        </button>
      )}
    </main>
  );
}

export interface QuizStationProps {
  quoteId: string;
  planningFor: "self" | "loved_one";
  screenRef: string;
  responseAnimationVariant: QuizResponseAnimationVariant;
  onNavigate: (screenRef: string) => void;
  /** Every answer change; `reachedEnd` when this answer took the quiz past
   * its last question, so the flow may move to the station the conclusion
   * derives — a mid-quiz edit syncs but stays in the quiz. */
  onAnswersChange: (answers: Answers, reachedEnd: boolean) => void;
}

export default function QuizStation({
  quoteId,
  planningFor,
  screenRef,
  responseAnimationVariant,
  onNavigate,
  onAnswersChange,
}: QuizStationProps) {
  const { track } = useQuoteFlowAnalytics();
  const [answers, setAnswers] = useState<Answers>(
    () => loadQuiz(quoteId)?.answers ?? {},
  );
  const [awaitingAdvance, setAwaitingAdvance] = useState(false);
  const [retryAvailable, setRetryAvailable] = useState(false);

  useEffect(() => {
    if (!awaitingAdvance) {
      setRetryAvailable(false);
      return;
    }
    const timer = setTimeout(
      () => setRetryAvailable(true),
      ADVANCE_RETRY_AFTER_MS,
    );
    return () => clearTimeout(timer);
  }, [awaitingAdvance]);

  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    track("quote_flow_quiz_started", {});
  }, [track]);

  useEffect(() => {
    const shown = questionBySlug(screenRef) ?? QUIZ_QUESTIONS[0];
    track("build_quote_question_viewed", {
      question_key: shown.key,
      question_step: stepNumber(shown.key),
      total_questions: QUIZ_QUESTIONS.length,
    });
    // Fires once per screen shown, not on every render that leaves it
    // untouched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenRef]);

  const question = questionBySlug(screenRef) ?? QUIZ_QUESTIONS[0];
  const preselectedWho = planningFor === "self" ? "myself" : "loved_one";
  const addressingLovedOne = answers.who === "loved_one";

  const commit = (value: string | string[]) => {
    const updated: Answers = { ...answers, [question.key]: value };
    const destination = nextScreen(
      QUIZ_QUESTIONS,
      question.key,
      answers,
      value,
    );
    setAnswers(updated);
    saveQuiz(quoteId, {
      screen: slugFor(
        destination === QUIZ_COMPLETE ? question.key : destination,
      ),
      answers: updated,
    });
    onAnswersChange(updated, destination === QUIZ_COMPLETE);

    if (question.type !== "response") {
      track("build_quote_question_answered", {
        question_key: question.key,
        answer: value,
        question_step: stepNumber(question.key),
        total_questions: QUIZ_QUESTIONS.length,
      });
      track("quote_flow_quiz_question_answered", {
        question_key: question.key,
        answer: value,
      });
    }

    if (destination === QUIZ_COMPLETE) {
      setAwaitingAdvance(true);
      return;
    }
    onNavigate(slugFor(destination));
  };

  const back = previousScreen(QUIZ_QUESTIONS, question.key, answers);
  const handleBack = () => {
    if (!back) return;
    track("build_quote_back_clicked", {
      from_step: stepNumber(question.key),
      from_step_key: question.key,
      to_step: stepNumber(back),
      to_step_key: back,
    });
    // The previous screen by the quiz's own rules, as a forward navigation
    // — the same way the application steps back — so it holds no matter
    // how this screen was reached (browser history, the schedule-call
    // screen's Back, a reload).
    onNavigate(slugFor(back));
  };

  if (awaitingAdvance) {
    return (
      <AwaitingAdvanceScreen
        showRetry={retryAvailable}
        onRetry={() => {
          setRetryAvailable(false);
          // The same commit signal a fresh answer sends — the parent's
          // sync re-derives from these answers and retries whatever
          // request didn't make it through.
          onAnswersChange(answers, true);
        }}
      />
    );
  }

  return (
    <>
      <QuizProgressBar questionKey={question.key} />
      <QuizQuestionScreen
        question={question}
        answers={answers}
        addressingLovedOne={addressingLovedOne}
        preselectedWho={preselectedWho}
        responseAnimationVariant={responseAnimationVariant}
        onAnswer={commit}
        onBack={back ? handleBack : undefined}
      />
    </>
  );
}
